import { useParams } from "react-router-dom";

import { PollingIntervalGroups } from "apollo/constants";
import usePolledQuery from "apollo/usePolledQuery";
import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import useErrorHandle from "hooks/useErrorHandle";
import useTypedContext from "hooks/useTypedContext";
import { Module } from "types/generated";
import Run from "components/Run";

import { ModuleContext } from "../../Context";
import useModuleVersionResolver from "../../useLatestModuleVersion";
import { GET_VERSION_RUN } from "./gql";

type GetVersionRunGql = {
  module: Module;
};

type VersionRunRouteParams = { runId: string; versionId: string };

const ModuleVersionRun = () => {
  const { runId, versionId } = useParams<VersionRunRouteParams>();
  const { module } = useTypedContext(ModuleContext);

  const versionResolver = useModuleVersionResolver(versionId as string, module);

  const { data, error, loading } = usePolledQuery<GetVersionRunGql>(GET_VERSION_RUN, {
    shouldPoll: (data) => data?.module?.run?.finished === false,
    pollingGroup: PollingIntervalGroups.SingleEntities,
    variables: { moduleId: module.id, runId, ...versionResolver },
  });

  useBreadcrumbs(
    [
      {
        title: "Modules",
        link: "/modules",
      },
      {
        title: module.id,
        link: `/module/${module.id}`,
      },
      {
        title: (data?.module?.versionFind?.number && `Version ${data?.module?.versionFind?.number}`) || "",
        link: `/module/${module.id}/version/${versionId}/overview/tests`,
      },
      {
        title: data?.module?.run?.title || "",
      },
    ],
    [data?.module?.run?.title, data?.module?.versionFind?.number]
  );

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data?.module?.run) {
    return <PageLoading />;
  }

  if (!data?.module || !data?.module?.run) {
    return <NotFoundPage />;
  }

  return <Run run={data.module.run} stack={module} versionId={versionId} />;
};

export default ModuleVersionRun;
